import React from 'react'
import ProfessionalChartLoader from '../../../../common/components/molecule/Chart/ChartLoaderView/ProfessionalChartLoader'
import ChartErrorView from '../../../../common/components/molecule/Chart/ChartLoaderView/ChartErrorView'
import { createList } from '../../../../common/utils/graph.utils'
import { formatDateByPeriod } from '../../../../common/utils/dateFormatter'
import ChartLineView from '../../../../common/components/molecule/Chart/ChartLineView/ChartLineView'
import { AggregateWindow } from '../../../../features/filter/types/types'
import { MomiTelemetryFilter } from '../../service/momiTelemetryAPI'
import { useMomiTelemetryMeasurement } from '../../hooks/useMomiTelemetryMeasurement'
import {
  buildMomiTelemetryMultiSeries,
  momiTelemetryShouldUseMultiSeries,
} from './momiTelemetryChart.utils'

type Props = {
  momiUuid: string
  measurement: string
  title: string
  label: string
  unit: string
  filter: MomiTelemetryFilter
  scale?: number
}

export default function MomiTelemetryLineChartPresenter({
  momiUuid,
  measurement,
  title,
  label,
  unit,
  filter,
  scale,
}: Props) {
  const query = useMomiTelemetryMeasurement(momiUuid, measurement, filter)

  if (query.isLoading) {
    return <ProfessionalChartLoader metricName={title} />
  }

  if (query.isError) {
    return <ChartErrorView metricName={title} />
  }

  const aggWindow = (filter.aggregateWindow || '1h') as AggregateWindow
  const filterPoint = {
    startDate: filter.startDate,
    stopDate: filter.stopDate,
    aggregateWindow: aggWindow,
  }
  const points = query.data ?? []
  const rawMultiSeries = momiTelemetryShouldUseMultiSeries(points)
    ? buildMomiTelemetryMultiSeries(aggWindow, filterPoint, points, label)
    : undefined

  const dataChart = createList(aggWindow, filterPoint, points)

  const scaleValue = (v: number | null) =>
    v == null || scale == null ? v : v * scale

  const scaledData = scale != null ? dataChart.listValue.map(scaleValue) : dataChart.listValue
  const multiSeries = scale != null
    ? rawMultiSeries?.map((s) => ({
        ...s,
        data: s.data.map((d) => ({ ...d, value: scaleValue(d.value) })),
      }))
    : rawMultiSeries

  return (
    <ChartLineView
      multiSeries={multiSeries}
      xAxisData={dataChart.listx.map((item) => formatDateByPeriod(item, aggWindow))}
      data={scaledData}
      label={label}
      unit={unit}
    />
  )
}
